import { supabase } from './supabase';
import { getCurrentTenantId } from '@/utils/tenantUtils';
import { getGoogleOAuthCredentials } from '@/utils/oauthHelper';
// Make sure redirect URI matches exactly what's configured in Google Cloud Console
// Important: Don't include query parameters in the redirect URI to avoid OAuth errors
const REDIRECT_URI = `${window.location.origin}/integration-callback`;

// Google OAuth Configuration

// Track ongoing token exchanges
let tokenExchangeInProgress = false;

const SCOPES = [
  'https://www.googleapis.com/auth/calendar.events',
  'https://www.googleapis.com/auth/calendar.readonly',
].join(' ');

/**
 * Initiate Google OAuth flow in a popup window
 * @param {string} userId - User ID to associate with the integration
 * @returns {Promise<boolean>} Promise that resolves to true if integration was successful
 */
export const initiateGoogleAuthFlow = (userId: string): Promise<boolean> => {
  return new Promise(async (resolve) => {
    try {
      // Get tenant-specific OAuth credentials
      const { clientId } = await getGoogleOAuthCredentials();
      
      
      // Create the OAuth URL
      const url = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      url.searchParams.append('client_id', clientId);
      url.searchParams.append('redirect_uri', REDIRECT_URI);
      url.searchParams.append('response_type', 'code');
      url.searchParams.append('scope', SCOPES);
      url.searchParams.append('access_type', 'offline');
      url.searchParams.append('prompt', 'consent');
    
    // Pack userId and integration type in state parameter as JSON
    const stateData = JSON.stringify({
      userId: userId,
      type: 'google_calendar'
    });
    url.searchParams.append('state', stateData);
    
    // Features for the popup window
    const windowFeatures = {
      width: 600,
      height: 700,
      menubar: 'no',
      toolbar: 'no',
      location: 'yes',
      resizable: 'yes',
      scrollbars: 'yes',
      status: 'yes'
    };
    
    // Open the popup
    const popup = window.open(
      url.toString(),
      'googleAuthPopup',
      Object.entries(windowFeatures)
        .map(([key, value]) => `${key}=${value}`)
        .join(',')
    );
    
    // Set up message listener to receive the result
    const messageListener = (event: MessageEvent) => {
      // Verify the origin and source
      if (event.origin !== window.location.origin || 
          !event.data || 
          event.data.source !== 'solerro-integration-callback') {
        return;
      }
      
      // Handle the message
      
      // Clean up
      window.removeEventListener('message', messageListener);
      
      // NOTE: We no longer automatically close the popup window
      // Let the user close it manually by clicking the "Click to close" button
      
      // Resolve the promise with the success status
      resolve(event.data.success);
    };
    
    // Add the message listener
    window.addEventListener('message', messageListener);
    
      // Fallback: If the popup is closed without sending a message
      const checkPopupClosed = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(checkPopupClosed);
          window.removeEventListener('message', messageListener);
          resolve(false);
        }
      }, 1000);
    } catch (error) {
      console.error('Error initiating Google Calendar OAuth flow:', error);
      resolve(false);
    }
  });
};

/**
 * Generate Google OAuth authentication URL (legacy method for direct navigation)
 * @returns {Promise<string>} Authentication URL
 */
export const getGoogleAuthUrl = async (): Promise<string> => {
  const { clientId } = await getGoogleOAuthCredentials();
  
  const url = new URL('https://accounts.google.com/o/oauth2/v2/auth');
  url.searchParams.append('client_id', clientId);
  url.searchParams.append('redirect_uri', REDIRECT_URI);
  url.searchParams.append('response_type', 'code');
  url.searchParams.append('scope', SCOPES);
  url.searchParams.append('access_type', 'offline');
  url.searchParams.append('prompt', 'consent');
  
  return url.toString();
};

/**
 * Get user's calendar timezone
 * @param {string} userId User ID
 * @returns {Promise<string|null>} Calendar timezone or null if not available
 */
export const getCalendarTimezone = async (userId: string): Promise<string | null> => {
  try {
    if (!userId) {
      console.error('No user ID provided for getCalendarTimezone');
      return null;
    }
    
    const accessToken = await getValidAccessToken(userId);
    if (!accessToken) {
      console.error('No valid access token available for getCalendarTimezone');
      return null;
    }
    
    const response = await fetch(
      'https://www.googleapis.com/calendar/v3/users/me/settings/timezone',
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error fetching calendar timezone:', errorText);
      return null;
    }

    const data = await response.json();
    return data.value;
  } catch (error) {
    console.error('Error getting calendar timezone:', error);
    return null;
  }
};

/**
 * Handle Google OAuth callback
 * @param {string} code Authorization code
 * @returns {Promise<{success: boolean, userId?: string, error?: string}>} Result
 */
export const handleGoogleCallback = async (code: string): Promise<{success: boolean, userId?: string, error?: string}> => {
  try {
    
    if (tokenExchangeInProgress) {
      return { success: false, error: 'Token exchange already in progress' };
    }
    
    tokenExchangeInProgress = true;
    
    // Get the current user
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.error('No authenticated user found');
      tokenExchangeInProgress = false;
      return { success: false, error: 'User not authenticated' };
    }
    
    // Get user's tenant_id from profiles
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('tenant_id')
      .eq('id', user.id)
      .single();
    
    
    // Get current tenant ID dynamically
    let tenantId: string;
    try {
      const tenantIdResult = await getCurrentTenantId();
      if (!tenantIdResult) {
        throw new Error('No tenant ID available');
      }
      tenantId = tenantIdResult;
    } catch (error) {
      console.warn('❌ Error getting current tenant ID, using Joules fallback:', error);
      tenantId = '511f29e8-bef3-4065-9927-777d6eafe7f1';
    }
    
    // Get tenant-specific OAuth credentials
    const { clientId, clientSecret } = await getGoogleOAuthCredentials();
    
    // Exchange the code for tokens
    const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        code,
        client_id: clientId,
        client_secret: clientSecret,
        redirect_uri: REDIRECT_URI,
        grant_type: 'authorization_code',
      }),
    });
    
    if (!tokenResponse.ok) {
      const errorText = await tokenResponse.text();
      console.error('Error exchanging code for tokens:', errorText);
      tokenExchangeInProgress = false;
      return { success: false, error: 'Failed to exchange code for tokens' };
    }
    
    const tokens = await tokenResponse.json();
    
    // Store the tokens in Supabase
    // Check both table names to handle possible database schema differences
    const { data: existingToken, error: fetchError } = await supabase
      .from('google_calendar_tokens')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();
    
    if (fetchError && fetchError.code !== 'PGRST116') {
      console.error('Error fetching existing token:', fetchError);
      tokenExchangeInProgress = false;
      return { success: false, error: 'Database error: ' + fetchError.message };
    }
    
    const now = new Date().toISOString();
    const expiresAt = new Date(Date.now() + tokens.expires_in * 1000).toISOString();
    
    let updateError;
    
    // First try to save to google_calendar_tokens table
    if (existingToken) {
      // Update existing record
      
      
      const { error } = await supabase
        .from('google_calendar_tokens')
        .update({
          access_token: tokens.access_token,
          refresh_token: tokens.refresh_token || existingToken.refresh_token,
          expires_at: expiresAt,
          tenant_id: tenantId, // Use dynamic tenant_id from user profile
          // updated_at is handled by the trigger
        })
        .eq('id', existingToken.id);
      
      updateError = error;
      if (error) {
        console.error('Failed to update token record:', error);
      } else {
      }
    } else {
      // Create new record
      
      const insertPayload = {
        user_id: user.id,
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
        expires_at: expiresAt,
        tenant_id: tenantId, // Use dynamic tenant_id from user profile
      };
      
      const { error, data } = await supabase
        .from('google_calendar_tokens')
        .insert(insertPayload)
        .select();
      
      updateError = error;
      if (error) {
        console.error('Failed to create token record:', error);
      } else {
      }
    }
    
    // If the primary table operation failed, try the alternative table name as a fallback
    if (updateError) {
      // Try to save to google_calendar_integrations table as fallback
      const { data: existingIntegration } = await supabase
        .from('google_calendar_integrations')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
        
      if (existingIntegration) {
        const { error } = await supabase
          .from('google_calendar_integrations')
          .update({
            access_token: tokens.access_token,
            refresh_token: tokens.refresh_token || existingIntegration.refresh_token,
            expires_at: expiresAt,
            tenant_id: tenantId, // Use dynamic tenant_id from user profile
            updated_at: now,
          })
          .eq('id', existingIntegration.id);
          
        if (error) {
          console.error('Failed to update fallback integration record:', error);
        } else {
          updateError = null; // Clear the error since fallback succeeded
        }
      } else {
        const { error } = await supabase
          .from('google_calendar_integrations')
          .insert({
            user_id: user.id,
            access_token: tokens.access_token,
            refresh_token: tokens.refresh_token,
            expires_at: expiresAt,
            tenant_id: tenantId, // Use dynamic tenant_id from user profile
            created_at: now,
            updated_at: now,
          });
          
        if (error) {
          console.error('Failed to create fallback integration record:', error);
        } else {
          updateError = null; // Clear the error since fallback succeeded
        }
      }
    }
    
    if (updateError) {
      console.error('Error storing tokens:', updateError);
      tokenExchangeInProgress = false;
      return { success: false, error: 'Failed to store tokens' };
    }
    
    // Update the user's profile to indicate Google Calendar is connected
    // First check if the profile exists
    const { data: profileData, error: profileCheckError } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', user.id)
      .single();
      
    
    if (profileCheckError) {
      console.error('Error checking profile:', profileCheckError);
      
      // If profile doesn't exist, create it
      if (profileCheckError.code === 'PGRST116') {
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: user.id,
            google_calendar_connected: true,
            google_refresh_token: tokens.refresh_token,
            google_access_token: tokens.access_token,
            google_token_expiry: expiresAt
          });
          
        if (insertError) {
          console.error('Error creating profile:', insertError);
        } else {
        }
      }
    } else {
      // Profile exists, update it
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          google_calendar_connected: true,
          google_refresh_token: tokens.refresh_token || (existingToken?.refresh_token || null),
          google_access_token: tokens.access_token,
          google_token_expiry: expiresAt
        })
        .eq('id', user.id);
      
      if (profileError) {
        console.error('Error updating profile:', profileError);
      } else {
      }
    }
    
    tokenExchangeInProgress = false;
    return { success: true, userId: user.id };
  } catch (error) {
    console.error('Error in Google callback:', error);
    tokenExchangeInProgress = false;
    return { success: false, error: 'Unexpected error during authentication' };
  }
};

/**
 * Refresh Google OAuth access token
 * @param {string} userId User ID
 * @returns {Promise<string|null>} New access token or null on failure
 */
export const refreshAccessToken = async (userId: string): Promise<string | null> => {
  try {
    // Get tenant-specific OAuth credentials
    const { clientId, clientSecret } = await getGoogleOAuthCredentials();
    
    // Fetch the refresh token from the database
    const { data, error } = await supabase
      .from('google_calendar_tokens')
      .select('refresh_token')
      .eq('user_id', userId)
      .single();
    
    if (error || !data?.refresh_token) {
      console.error('Error fetching refresh token:', error);
      return null;
    }
    
    // Exchange the refresh token for a new access token
    const response = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        refresh_token: data.refresh_token,
        client_id: clientId,
        client_secret: clientSecret,
        grant_type: 'refresh_token',
      }),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error refreshing access token:', errorText);
      
      // If refresh token is invalid, clear the token
      if (response.status === 400) {
        await supabase
          .from('google_calendar_tokens')
          .delete()
          .eq('user_id', userId);
      }
      
      return null;
    }
    
    const tokens = await response.json();
    const expiresAt = new Date(Date.now() + tokens.expires_in * 1000).toISOString();
    
    // Update the database with the new access token
    const { error: updateError } = await supabase
      .from('google_calendar_tokens')
      .update({
        access_token: tokens.access_token,
        expires_at: expiresAt,
        // updated_at is handled by database trigger
      })
      .eq('user_id', userId);
    
    if (updateError) {
      console.error('Error updating access token:', updateError);
      return null;
    }
    
    return tokens.access_token;
  } catch (error) {
    console.error('Error refreshing access token:', error);
    return null;
  }
};

/**
 * Get a valid access token, refreshing if necessary
 * @param {string} userId User ID
 * @returns {Promise<string|null>} Valid access token or null on failure
 */
export const getValidAccessToken = async (userId: string): Promise<string | null> => {
  try {
    // Fetch the access token from the database
    const { data, error } = await supabase
      .from('google_calendar_tokens')
      .select('access_token, expires_at')
      .eq('user_id', userId)
      .single();
    
    if (error) {
      console.error('Error fetching access token:', error);
      return null;
    }
    
    if (!data) {
      console.error('No Google Calendar token found for user:', userId);
      return null;
    }
    
    // Check if the token is expired or about to expire
    const expiresAt = new Date(data.expires_at);
    const now = new Date();
    const isExpired = expiresAt <= now;
    
    // Add a 5-minute buffer to refresh tokens that are about to expire
    const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
    const isAboutToExpire = expiresAt.getTime() - now.getTime() < bufferTime;
    
    if (isExpired || isAboutToExpire) {
      // Refresh the token
      return await refreshAccessToken(userId);
    }
    
    return data.access_token;
  } catch (error) {
    console.error('Error getting valid access token:', error);
    return null;
  }
};

/**
 * List events from user's Google Calendar
 * @param {string} userId User ID
 * @param {string} timeMin Start time in ISO format
 * @param {string} timeMax End time in ISO format
 * @param {string} calendarId Optional calendar ID (defaults to primary)
 * @returns {Promise<any[]>} List of calendar events or empty array on failure
 */
export const listCalendarEvents = async (
  userId: string,
  timeMin: string,
  timeMax: string,
  calendarId = 'primary'
): Promise<any[]> => {
  try {
    const accessToken = await getValidAccessToken(userId);
    if (!accessToken) {
      return [];
    }
    
    const params = new URLSearchParams({
      timeMin,
      timeMax,
      singleEvents: 'true',
      orderBy: 'startTime',
    });
    
    const response = await fetch(
      `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events?${params}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error fetching calendar events:', errorText);
      return [];
    }
    
    const data = await response.json();
    return data.items || [];
  } catch (error) {
    console.error('Error listing calendar events:', error);
    return [];
  }
};

/**
 * List all calendars for a user (only owned calendars, not subscribed ones)
 * @param {string} userId User ID
 * @returns {Promise<any[]>} List of owned calendars or empty array on failure
 */
export const listUserCalendars = async (userId: string): Promise<any[]> => {
  try {
    const accessToken = await getValidAccessToken(userId);
    if (!accessToken) {
      return [];
    }
    
    const response = await fetch(
      'https://www.googleapis.com/calendar/v3/users/me/calendarList',
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error fetching user calendars:', errorText);
      return [];
    }
    
    const data = await response.json();
    const allCalendars = data.items || [];
    
    // Filter to only include calendars that the user owns
    // Google Calendar API returns accessRole property that indicates ownership
    // "owner" means the user owns the calendar
    // "writer" means the user can write to it but doesn't own it
    // "reader" means the user can only read it
    const ownedCalendars = allCalendars.filter((calendar: any) => {
      return calendar.accessRole === 'owner';
    });
    
    return ownedCalendars;
  } catch (error) {
    console.error('Error listing user calendars:', error);
    return [];
  }
};

/**
 * Create a calendar event
 * @param {string} userId User ID
 * @param {string} calendarId Calendar ID
 * @param {object} event Event data
 * @returns {Promise<any|null>} Created event data or null on failure
 */
export const createCalendarEvent = async (
  userId: string,
  calendarId: string,
  event: {
    summary: string;
    description?: string;
    location?: string;
    start: { dateTime: string; timeZone?: string };
    end: { dateTime: string; timeZone?: string };
    attendees?: { email: string; name?: string }[];
    conferenceData?: {
      createRequest: {
        requestId: string;
        conferenceSolutionKey: {
          type: string;
        };
      };
    };
  }
): Promise<any | null> => {
  try {
    const accessToken = await getValidAccessToken(userId);
    if (!accessToken) {
      return null;
    }
    
    const response = await fetch(
      `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(event),
      }
    );
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error creating calendar event:', errorText);
      return null;
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating calendar event:', error);
    return null;
  }
};

/**
 * Get Google Calendar settings URL
 * @returns {string} Google Calendar settings URL
 */
export const getGoogleCalendarSettingsUrl = (): string => {
  return 'https://calendar.google.com/calendar/u/0/r/settings';
};
